import { Component, Input, OnInit } from '@angular/core';
import { AppBaseDataService } from 'src/app/_services/app-base-data.service';
import { BusinessSetupService } from 'src/app/_services/business-setup.service';
import { ConnMessageService } from 'src/app/_services/conn-message.service';
import { SCV2_CARRIERS_TAB_ENABLED, SCV2_LABELS_DASHBOARD_ENABLED, ShippingConfigV2Service } from './shipping-config-v2.service';

@Component({
  selector: 'app-shipping-configuration-v2',
  templateUrl: './shipping-configuration-v2.component.html',
  styleUrls: ['./shipping-configuration-v2.component.scss'],
})
export class ShippingConfigurationV2Component implements OnInit {
  // kept for parity with the existing Shipping Configuration tab wiring; unused here.
  @Input() singupBusinessConfig = false;

  // Full tab set kept intact; carriers/labels-dashboard tabs are filtered out while hidden.
  // Numbering is derived from position among the *visible* tabs (below) rather than hardcoded here,
  // so hiding any tab renumbers the rest instead of leaving a gap (e.g. "2. Shipping rules" as the
  // only tab). TODO re-enable carriers tab / labels dashboard.
  private allSubTabs: { id: 'carriers' | 'rules' | 'dashboard'; label: string }[] = [
    { id: 'carriers', label: 'Connect carriers' },
    { id: 'rules', label: 'Shipping rules' },
    { id: 'dashboard', label: 'Shipping labels' },
  ];

  subTabs = this.allSubTabs
    .filter((t) =>
      (t.id !== 'carriers' || SCV2_CARRIERS_TAB_ENABLED) &&
      (t.id !== 'dashboard' || SCV2_LABELS_DASHBOARD_ENABLED)
    )
    .map((t, i) => ({ ...t, label: `${i + 1}. ${t.label}` }));

  isLoadingShippingState = false;
  isTogglingShipping = false;

  // Raw zone objects exactly as returned by GetShippingConfiguration — round-tripped as-is (with
  // only `isEnabled` overridden) when the master gate is toggled, so PUT webstore/shipping never
  // clobbers zone fields (flatRate/storePickUp/usps ids, address, etc.) the Rules tab has saved.
  private rawZones: any[] = [];

  constructor(
    public service: ShippingConfigV2Service,
    private _businessSetupService: BusinessSetupService,
    private _connMessageService: ConnMessageService,
    private _abd: AppBaseDataService,
  ) {}

  ngOnInit(): void {
    // The service's default activeSubTab ('carriers') may be hidden — land on the first visible
    // tab instead of showing a blank pane with no matching *ngIf branch.
    if (!this.subTabs.some((t) => t.id === this.service.activeSubTab)) {
      this.service.activeSubTab = this.subTabs[0]?.id ?? 'rules';
    }
    this.loadShippingEnabledState();
  }

  get enabled(): boolean {
    return this.service.shippingEnabled;
  }

  get activeSubTab(): 'carriers' | 'rules' | 'dashboard' {
    return this.service.activeSubTab;
  }

  setSubTab(id: 'carriers' | 'rules' | 'dashboard'): void {
    this.service.activeSubTab = id;
  }

  /** Hydrates the master "Do you ship?" gate from the real webstore/shipping zones instead of the
   *  in-memory default — V1 applies a single isEnabled flag across every zone on save, so they're
   *  always in lockstep; reading the first zone's isEnabled reflects what's actually persisted. */
  private loadShippingEnabledState(): void {
    this.isLoadingShippingState = true;
    this._businessSetupService.getShippingConfiguration().subscribe({
      next: (res: any) => {
        this.isLoadingShippingState = false;
        const rawData = res?.data ?? res?.Data;
        const zones = Array.isArray(rawData) ? rawData : (rawData?.zones ?? []);
        this.rawZones = Array.isArray(zones) ? zones : [];
        this.service.shippingEnabled = this.rawZones.length > 0 && this.rawZones[0]?.isEnabled !== false;
      },
      error: () => {
        this.isLoadingShippingState = false;
        // No saved config (or endpoint not reachable): nothing configured yet, so there's nothing
        // to flip — leave the gate off rather than showing a state that isn't actually persisted.
        this.rawZones = [];
        this.service.shippingEnabled = false;
      },
    });
  }

  /** Master "Do you ship?" gate — mirrors V1's shippingEnabled flag by flipping `isEnabled` across
   *  every zone and persisting via the same webstore/shipping PUT the Rules tab uses, instead of
   *  just flipping an in-memory flag. If nothing is configured yet there's nothing to persist —
   *  flip the in-memory flag only; it becomes real once a zone is added and saved from Rules. */
  onEnabledToggleRequested(nextValue: boolean): void {
    if (this.isTogglingShipping) {
      return;
    }

    if (!this.rawZones.length) {
      this.service.shippingEnabled = nextValue;
      return;
    }

    const payload = { zones: this.rawZones.map((zone: any) => ({ ...zone, isEnabled: nextValue })) };

    this.isTogglingShipping = true;
    this._abd.showSpinner();
    this._businessSetupService.updateShippingConfiguration(null, payload).subscribe({
      next: (res: any) => {
        this.isTogglingShipping = false;
        this._abd.hideSpinner();
        if (res?.success === false) {
          this._connMessageService.showToastMessage(res?.message || 'Failed to update shipping.', 'error');
          return;
        }
        this.rawZones = this.rawZones.map((zone: any) => ({ ...zone, isEnabled: nextValue }));
        this.service.shippingEnabled = nextValue;
        this._connMessageService.showToastMessage(nextValue ? 'Shipping turned on.' : 'Shipping turned off.', 'success');
      },
      error: () => {
        this.isTogglingShipping = false;
        this._abd.hideSpinner();
        this._connMessageService.showToastMessage('Error updating shipping!', 'error');
      },
    });
  }
}
